/**
 * @fileoverview Organization selector menu for the user dropdown.
 * @module features/orgs/components/OrgSelectorMenu
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu'
import { useActiveOrg } from '../context/ActiveOrgProvider'
import { switchOrg } from '@/features/preferences/api/preferencesService'
import { toast } from 'sonner'
import { queryClient } from '@/App'

/**
 * Renders a radio group of organizations inside a dropdown menu.
 * @component
 */
export function OrgSelectorMenu(): ReactElement | null {
  const { t } = useTranslation('features-orgs')
  const { orgs, activeOrgId, refreshFromProfile } = useActiveOrg()
  if (!orgs?.length) return null

  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {t('organization.label')}
        </DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={(activeOrgId as unknown as string) ?? ''}
          onValueChange={() => {
            /* handled per-item onSelect */
          }}
        >
          {orgs.map(org => (
            <DropdownMenuRadioItem
              key={org.id as unknown as string}
              value={org.id as unknown as string}
              onSelect={async () => {
                try {
                  const res = await switchOrg({ organizationId: org.id as unknown as never })
                  // Clear query cache since query keys are not org-scoped
                  // This prevents stale data from previous org being shown
                  queryClient.clear()
                  refreshFromProfile({
                    organizations: res.organizations,
                    defaultOrgId: (res.defaultOrgId ?? null) as unknown as never,
                    ...(res.preferredLanguage ? { preferredLanguage: res.preferredLanguage } : {}),
                  })
                } catch (e) {
                  const status = (e as { response?: { status?: number } })?.response?.status
                  if (status === 403) {
                    toast.error('You are not a member of that organization.')
                  } else {
                    toast.error('Failed to switch organization')
                  }
                }
              }}
            >
              {org.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuGroup>
    </>
  )
}
